<template>
  <section id="use-case" class="relative">
    <img
      src="@images/landing-page/rounded-bars-2.svg"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute -translate-x-[10rem] translate-y-[20rem] scale-200 rotate-[150deg] opacity-20 md:-translate-x-[25rem] md:translate-y-10 md:scale-100 lg:-translate-x-[20rem]"
      loading="lazy"
    />

    <div class="relative mx-auto max-w-[85rem] space-y-20 px-4">
      <div class="z-auto mb-5 space-y-3">
        <h2 class="text-center font-exo-bold text-5xl text-pretty uppercase lg:text-6xl">
          Where <span class="bg-linear-to-r from-mauve-500 to-navy-blue-500 bg-clip-text text-transparent">Smart Decisions</span>
          Happen
        </h2>
        <p class="text-center text-eerie-black-200">
          Learn how collaborative AI teams refine ideas and uncover insights<br class="hidden lg:flex" />
          beyond what one AI can provide.
        </p>
      </div>

      <div class="hidden h-[400px] w-full items-center justify-center gap-4 overflow-hidden md:flex">
        <div
          v-for="(item, index) in images"
          :key="index"
          class="relative h-full cursor-pointer rounded-[1rem] bg-gradient-to-br from-mauve-500 to-navy-blue-500 p-[1px] opacity-50 transition-[width,opacity] duration-300 ease-[cubic-bezier(0.05,0.61,0.41,0.95)]"
          :class="activeIndex === index ? 'w-full opacity-100' : 'w-[100px]'"
          @mouseenter="onEnter(index)"
        >
          <div class="relative h-full w-full overflow-hidden rounded-[calc(1rem-1px)] bg-white">
            <div
              class="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 text-mauve-500 transition-transform duration-300"
              :class="activeIndex === index ? 'scale-0' : 'scale-100'"
            >
              <component :is="item.icon" class="h-8 w-8" />
            </div>
            <div class="relative h-full w-full">
              <div class="absolute inset-0 z-10 bg-gradient-to-t from-black/80 to-transparent"></div>
              <img
                :src="item.src"
                :alt="item.title"
                :class="index === 0 || index === 1 ? 'object-top' : ''"
                class="block h-full w-full object-cover"
              />
            </div>
            <div
              class="absolute bottom-4 left-4 z-20 flex flex-col gap-2 p-3 text-white transition-transform duration-300 ease-in-out"
              :class="activeIndex === index ? 'scale-100' : 'scale-0'"
            >
              <div>
                <component :is="item.icon" class="h-12 w-12 text-mauve-500" />
              </div>
              <h3 class="font-exo-bold text-3xl text-white">
                {{ item.title }}
              </h3>
              <p class="text-lg">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <swiper-container
        pagination="true"
        navigation="true"
        space-between="30"
        class="mySwiper block [--swiper-navigation-color:#ffffff] [--swiper-navigation-size:30px] [--swiper-pagination-bullet-inactive-color:#727272] [--swiper-pagination-bullet-inactive-opacity:1] [--swiper-pagination-bullet-size:16px] [--swiper-pagination-color:#e796fa] md:hidden"
      >
        <swiper-slide v-for="(item, index) in images" :key="index">
          <div class="relative h-full overflow-hidden rounded-[1rem] bg-gradient-to-br from-mauve-500 to-navy-blue-500 p-[1px]">
            <div class="relative overflow-hidden rounded-[1rem]">
              <div class="relative h-full w-full">
                <div class="absolute bottom-2 left-2 z-20 flex flex-col gap-2 px-2 pb-8">
                  <div>
                    <component :is="item.icon" class="h-10 w-10 text-mauve-500" />
                  </div>
                  <h3 class="font-exo-bold text-2xl text-white">
                    {{ item.title }}
                  </h3>
                  <p class="text-base">{{ item.description }}</p>
                </div>
                <div class="absolute inset-0 z-10 bg-gradient-to-t from-black to-transparent"></div>
                <img :src="item.src" :alt="item.title" class="h-[350px] w-full object-cover" width="600" height="338" loading="lazy" />
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
  </section>
</template>

<script lang="ts">
import { Bot, Brain, Briefcase, Users } from 'lucide-vue-next';

import UseCase1 from '@images/landing-page/ai-boardroom.webp';
import UseCase2 from '@images/landing-page/ai-career-and-health-consultant.webp';
import UseCase3 from '@images/landing-page/ai-parenting-circle.webp';
import UseCase4 from '@images/landing-page/custom-strategy-team.webp';

export default {
  name: 'UseCase',
  components: {
    Brain,
    Briefcase,
    Users,
    Bot,
  },
  data() {
    return {
      activeIndex: 0, // First item open on load
      images: [
        {
          icon: Brain,
          title: 'AI Boardroom',
          description: 'Get 2x faster C-suite decisions without boardroom bureaucracy.',
          src: UseCase1,
        },
        {
          icon: Briefcase,
          title: 'AI Career & Health Consultants',
          description: 'Accelerate career growth 3x faster with your all-in-one mentor.',
          src: UseCase2,
        },
        {
          icon: Users,
          title: 'AI Parenting Circle',
          description: 'Reduce parenting stress by 50% with tailored family wisdom.',
          src: UseCase3,
        },
        {
          icon: Bot,
          title: 'Custom Strategy Team',
          description: 'Cut strategy revision cycles by 40% — fortify your vision.',
          src: UseCase4,
        },
      ],
    };
  },
  methods: {
    onEnter(index: number) {
      this.activeIndex = index;
    },
  },
};
</script>
